import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  GASTOS_REPORT_TITULO_DEFAULT,
  applyGastosColumnLabels,
  buildTipoGastosLookup,
  buildGastosTipoSummaryRows,
  formatGastosCell,
  formatGastosPeriodoLegacy,
  groupGastosByTipo,
  mergeGastosEncabezado,
  resolveGastosGrandTotal,
  resolveGastosTipoGastoTexto,
  rowToGastosNorm,
  sumGastosImporteTotal,
  withGastosPeriodoLine,
} from './gastosReportModel';
import { columnWidthsProportionalMm, pdfTableInnerWidthMm } from './reporteEncabezadoPdf';
import { drawFiltrosSucursalesCajasPdf } from './reporteFiltrosEncabezadoExport';

function drawGastosEncabezadoPdf(doc, enc, pageW, margin, y0, form) {
  let y = y0;
  const e = enc || {};

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  if (e.nombreComercial) {
    doc.text(String(e.nombreComercial).toUpperCase(), pageW / 2, y, { align: 'center' });
    y += 5;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  if (e.domicilioFiscal) {
    doc.splitTextToSize(String(e.domicilioFiscal).trim(), pageW - margin * 2).forEach((ln) => {
      doc.text(ln, pageW / 2, y, { align: 'center' });
      y += 3.5;
    });
  }
  if (e.rfc) {
    doc.text(String(e.rfc).toUpperCase(), pageW / 2, y, { align: 'center' });
    y += 4;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(String(e.reporteTitulo || GASTOS_REPORT_TITULO_DEFAULT).toUpperCase(), pageW / 2, y, {
    align: 'center',
  });
  y += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  const periodo =
    e.periodoTexto || formatGastosPeriodoLegacy(form.fechaInicio, form.fechaFin);
  if (periodo) {
    doc.text(periodo, pageW / 2, y, { align: 'center' });
    y += 4;
  }
  y = drawFiltrosSucursalesCajasPdf(doc, e, pageW, margin, y, form);
  if (e.turnosTexto) {
    doc.text(`Turnos: ${String(e.turnosTexto)}`, pageW / 2, y, { align: 'center' });
    y += 4;
  } else if (form.turnoInicio != null || form.turnoFin != null) {
    doc.text(
      `Turnos: ${form.turnoInicio ?? '—'}  a  ${form.turnoFin ?? '—'}`,
      pageW / 2,
      y,
      { align: 'center' },
    );
    y += 4;
  }
  if (e.tipoGastoTexto) {
    doc.text(`Tipo de gasto: ${String(e.tipoGastoTexto)}`, pageW / 2, y, { align: 'center' });
    y += 4;
  }

  return y + 6;
}

/**
 * @param {object[]} rawRows
 * @param {object} options
 */
export function downloadGastosPdf(rawRows, options = {}) {
  if (!Array.isArray(rawRows)) return;

  const {
    fechaInicio,
    fechaFin,
    cajaInicio,
    cajaFin,
    turnoInicio,
    turnoFin,
    fileBase = 'gastos',
    encabezado = null,
    encabezadoFallback = null,
    sucursalNombre = '',
    reporteTitulo = GASTOS_REPORT_TITULO_DEFAULT,
    apiTotales = null,
    columnas = null,
    tiposCatalogo = [],
    tipoGastoFiltro = '',
    globalView = false,
  } = options;

  const form = { fechaInicio, fechaFin, cajaInicio, cajaFin, turnoInicio, turnoFin };
  const resolved = applyGastosColumnLabels(columnas, globalView);
  const tipoLookup = buildTipoGastosLookup(tiposCatalogo);
  const normRows = rawRows.map((r) => rowToGastosNorm(r, tipoLookup));
  const groups = groupGastosByTipo(normRows);
  const summaryRows = globalView ? buildGastosTipoSummaryRows(groups) : [];
  const grandTotal = resolveGastosGrandTotal(apiTotales, sumGastosImporteTotal(normRows));

  const encBase = mergeGastosEncabezado(encabezado, encabezadoFallback);
  const sn = String(sucursalNombre || '').trim();
  if (sn && !String(encBase.nombreComercial || '').toUpperCase().includes(sn.toUpperCase())) {
    encBase.nombreComercial = encBase.nombreComercial
      ? `${encBase.nombreComercial} - ${sn.toUpperCase()}`
      : sn.toUpperCase();
  }
  const encMerged = withGastosPeriodoLine(
    { ...encBase, reporteTitulo },
    fechaInicio,
    fechaFin,
    form
  );
  const tipoGastoTexto = resolveGastosTipoGastoTexto(
    tipoGastoFiltro,
    encMerged.tipoGastoTexto,
    tiposCatalogo,
  );
  if (tipoGastoTexto) encMerged.tipoGastoTexto = tipoGastoTexto;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 12;
  let y = drawGastosEncabezadoPdf(doc, encMerged, pageW, margin, margin, form);

  const head = [resolved.map((c) => c.label)];
  /** @type {Array<(string|{ content: string, colSpan?: number, styles?: object })[]>} */
  const body = [];
  /** @type {Set<number>} */
  const groupHeaderRows = new Set();
  /** @type {Set<number>} */
  const subtotalRows = new Set();

  if (globalView) {
    for (const r of summaryRows) {
      body.push(resolved.map((c) => formatGastosCell(c, r[c.id])));
    }
  } else {
    for (const g of groups) {
      groupHeaderRows.add(body.length);
      body.push([
        { content: g.label, colSpan: resolved.length, styles: { fontStyle: 'bold', halign: 'left' } },
      ]);
      for (const r of g.rows) {
        body.push(resolved.map((c) => formatGastosCell(c, r[c.id])));
      }
      subtotalRows.add(body.length);
      body.push([
        ...Array(Math.max(0, resolved.length - 1)).fill(''),
        formatGastosCell({ kind: 'money' }, g.subtotal),
      ]);
    }
  }

  const innerW = pdfTableInnerWidthMm(pageW, margin);
  const widthRatios = globalView ? [72, 28] : [38, 16, 86, 28];
  const widths = columnWidthsProportionalMm(widthRatios, innerW);

  autoTable(doc, {
    startY: y,
    tableWidth: innerW,
    head,
    body,
    theme: 'plain',
    tableLineColor: [0, 0, 0],
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      lineWidth: { top: 0.35, bottom: 0.15, left: 0, right: 0 },
      lineColor: [0, 0, 0],
    },
    bodyStyles: {
      textColor: [0, 0, 0],
      lineWidth: { top: 0, bottom: 0, left: 0, right: 0 },
    },
    styles: { fontSize: 8, cellPadding: { top: 1.2, bottom: 1.2, left: 1, right: 1 } },
    columnStyles: Object.fromEntries(
      resolved.map((c, i) => [i, { halign: c.align, cellWidth: widths[i] }])
    ),
    margin: { left: margin, right: margin },
    didParseCell: (data) => {
      if (data.section !== 'body') return;
      const rowIdx = data.row.index;
      if (groupHeaderRows.has(rowIdx)) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [255, 255, 255];
      }
      if (subtotalRows.has(rowIdx) && data.column.index === resolved.length - 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.lineWidth = { top: 0.5, bottom: 0, left: 0, right: 0 };
      }
    },
  });

  y = doc.lastAutoTable.finalY + 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Total de gastos', margin, y);
  doc.text(formatGastosCell({ kind: 'money' }, grandTotal), pageW - margin, y, { align: 'right' });

  doc.save(`${fileBase}-${fechaInicio || 'inicio'}-${fechaFin || 'fin'}.pdf`);
}
